import { Checkbox, Input, Modal, notification } from 'antd';
import { motion } from 'framer-motion';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';
import { DFormEntity } from '../../api/fetchDoctorGenieByOID';
import fetchUHIDConfig from '../../api/fetchUHIDConfig';
import fetchUHIDSuggest from '../../api/fetchUHIDSuggest';
import pushUHIDConfig from '../../api/pushUHIDConfig';
import { GENDERS, Gender } from '../../pages/NewPatient';
import { LS_NEW_PT_DF_AS, checkIfValidPhoneNumber, sanitizeSalutation } from '../../pages/utils';
import { Patient } from '../../store/types/patient';
import { track } from '../../tracking';
import PatientNameAutoSuggest from '../PatientNameAutoSuggest';
import PatientPhoneAutoSuggest, { getErrorComp } from '../PatientPhoneAutoSuggest';
import PatientUhidAutoSuggest from '../PatientUhidAutoSuggest';
import NewPatientAgeInput from './NewPatientAgeInput';
import TakePatientPic from './TakePatientPic';
import {
    DAYS,
    INVALID_PHONE_NUMBER,
    MALE_SALUTATION,
    MARITAL_STATUS,
    MONTHS,
    SALUTATION,
    YEARS,
} from './constants/add-patient';
import { MEDICAL_HISTORY_CATEGORY, MHC, MHItem2 } from '../../store/types/medicalHistory';
import { MedicalHistoryConfig } from '../../api/pushGenie';
import { cloneAndDelete, cloneAndUpsert } from '../../store/utils';
import { ConfigTabs } from '../prescriptionPad/rightPanel/workspace/padV2/ConfigureElementsTableDialog';
import CommonItemsMH from '../prescriptionPad/rightPanel/workspace/padV2/CommonItemsMH';
import { medicalHistoryOptions } from '../medicalHistoryConfigPanel/CustomiseSections';
import { MEDICAL_HISTORY_CONTAINER } from '../prescriptionPad/rightPanel/workspace/MedicalHistoryContainer';
import { AbhaIconNotLinked } from './Icons';
import { useSelector } from 'react-redux';
import { getMonthlyAbhaLoss } from '../../store/selectors/session';
import NoABDMPopver from './NoABDMPopver';
import { getDoc } from '../../store/idbHandler';
import CreateAbhaForm from '../abha/CreateAbhaForm';
import AbhaCard from '../abha/AbhaCard';
import PatientReferralAutoSuggest from '../PatientReferralAutoSuggest';
import { DYNAMIC_FORMS, FAMILY_MEMBERS_NAME } from '../../constants';
import PatientPhoneNumber from '../PatientPhoneNumber';
import { CountryCode } from 'libphonenumber-js';
import { useParams } from 'react-router-dom';
import { LANGS } from '../prescriptionPad/rightPanel/previewScreen/PreviewPanel';
import { RelationsInEditPatient } from '../RelatedProfiles/RelationsInEditPatient';

type Props = {
    phCode: string;
    isPhCode: boolean | undefined;
    phCodeEnum?: CountryCode;
    phoneNumber: string | undefined;
    isPhoneInputDisabled: boolean;
    name: string | undefined;
    isNameInputDisabled: boolean;
    isAgeInputDisabled: boolean;
    isGenderInputDisabled: boolean;
    isNameError: boolean;
    value: { id: YEARS | MONTHS | DAYS; label: string; value: number } | null;
    dob:
        | { dd?: string | undefined; mm?: string | undefined; yyyy?: string | undefined }
        | undefined;
    isDobError: boolean;
    isAgeDOBError?: boolean;
    gender: Gender | undefined;
    isGenderError: boolean;
    dForm?: DFormEntity[];
    dfValues: { [k: string]: string | number };
    onPatientAutoComplete: (p?: Patient) => void;
    onAddEkaRefer: (referDocId?: string, name?: string, number?: string) => void;
    onCountryCodeChange?: (text: string, countryCodeEnum: CountryCode) => void | undefined;
    onPhoneInputChange: (text?: string) => void;
    onNameInputChange: (text?: string) => void;
    onYYYYDobChange: (e: React.ChangeEvent<HTMLSelectElement> | string) => void;
    onMMDobChange: (e: React.ChangeEvent<HTMLSelectElement> | string) => void;
    onDDDobChange: (e: React.ChangeEvent<HTMLSelectElement> | string) => void;
    onAgeSelect: (item: { id: YEARS | MONTHS | DAYS; label: string; value: number } | null) => void;
    onDFChange: (df: DFormEntity) => (value: string) => void;
    onGenderChange: (gender?: Gender) => void;

    isPhoneNumberError: boolean;
    isReferralPhoneNumberError: boolean;
    setDialogVisibility: (visible: boolean, tab?: ConfigTabs) => void;
    showAddFields?: boolean;
    showUseABDM?: boolean;
    setPic: (imageFile?: File) => void;
    pic?: string | File;
    showMedicalHistoryInAddPt: boolean;
    ptMHHistory: MHC | undefined;
    medicalHistoryConfigData: MedicalHistoryConfig;
    setMedicalHistory: (mh: MHC | undefined) => void;
    toggleHasNoMedicalHistory?: () => void;
    hasNoMedicalHistory?: boolean;
    showMHConfigure?: boolean;
    patientEditFlow?: boolean;
    isAbdmEnabled?: boolean;
    onAutoComplete: (patient?: Patient | undefined) => void;
    ageDays: string | undefined;
    ageMonth: string | undefined;
    setFormStateHandle: (val: IFormState | undefined) => void;
    formState?: IFormState;
    clearDob?: () => void;
    showAbhaCard?: boolean;
    setShowAbhaCard: (val: boolean) => void;
    abhaId: string | undefined;
    setAbhaId: (abhaId: string | undefined) => void;
    setCreateSkipState: (createSkipState: string | undefined) => void;
    createSkipState: string | undefined;
    patientId?: string;
    authHealthId?: string | undefined;
    showSendOtpScreen?: boolean;
    setShowSendOtpScreen: (val: boolean) => void;
    abhaNewFlow: boolean | undefined;
    setIsPhoneNumberError: (val: boolean) => void;
    errorValidation: () => {
        success: boolean;
        dobStr?: string;
    };
    resetError: () => void;
    setAuthHealthId: (val: string | undefined) => void;
    onSendOtp?: () => void;
    isSendAbhaOtp?: boolean;
    fromNdhmDiscovery?: boolean;
    onRefresh: () => void;
    setAbhaOid: (val: string | undefined) => void;
};

const StatusMessage: {
    [key: string]: string;
} = {
    active: 'Y',
    inactive: 'N',
};

type IFormState = 'abha' | 'add-pt';

const PatientInfoForm: React.FC<Props> = ({
    phCode,
    isPhCode,
    phCodeEnum,
    phoneNumber,
    isPhoneInputDisabled,
    name,
    isNameInputDisabled,
    isAgeInputDisabled,
    isGenderInputDisabled,
    isNameError,
    value,
    dob,
    isDobError,
    gender,
    isGenderError,
    dForm,
    dfValues,
    onPatientAutoComplete,
    onCountryCodeChange,
    onPhoneInputChange,
    onNameInputChange,
    onAgeSelect,
    onDFChange,
    onGenderChange,
    onDDDobChange,
    onMMDobChange,
    onYYYYDobChange,
    isAbdmEnabled,
    isPhoneNumberError,
    setDialogVisibility,
    showAddFields,
    showUseABDM,
    setPic,
    pic,
    showMedicalHistoryInAddPt,
    setMedicalHistory,
    ptMHHistory,
    medicalHistoryConfigData,
    toggleHasNoMedicalHistory,
    hasNoMedicalHistory,
    showMHConfigure,
    patientEditFlow,
    isAgeDOBError,
    onAutoComplete,
    ageDays,
    ageMonth,
    formState,
    setFormStateHandle,
    clearDob,
    showAbhaCard,
    setShowAbhaCard,
    abhaId,
    createSkipState,
    setAbhaId,
    setCreateSkipState,
    patientId,
    setShowSendOtpScreen,
    authHealthId,
    showSendOtpScreen,
    abhaNewFlow,
    setIsPhoneNumberError,
    isReferralPhoneNumberError,
    errorValidation,
    resetError,
    setAuthHealthId,
    onAddEkaRefer,
    onSendOtp,
    isSendAbhaOtp,
    fromNdhmDiscovery,
    setAbhaOid,
}) => {
    const salutation = dForm?.find((t) => t.key === 'salutation');

    const [dyFormSuggestions, setDyFromSuggestions] = useState<{
        [k: string]: (string | number)[];
    }>({});

    useEffect(() => {
        setDyFromSuggestions(JSON.parse(localStorage.getItem(LS_NEW_PT_DF_AS) || '{}'));
    }, []);

    const currMonthLoss = useSelector(getMonthlyAbhaLoss);
    const params: { cId: string } = useParams();

    const dFormWithoutUHID = dForm?.filter((d) => d.key !== 'uhid');

    const uhidDForm = dForm?.find((d) => d.key === 'uhid');

    const onAddFieldsClick = () => {
        setDialogVisibility(true);
    };

    const today = moment(new Date());

    const uhid = uhidDForm && dfValues[uhidDForm.key]?.toString();

    const onUhidInputChange = (input: string): void => {
        uhidDForm && onDFChange(uhidDForm)(input);
    };

    const salutationChangeHandler = (e?: React.ChangeEvent<HTMLSelectElement>): void => {
        salutation && onDFChange(salutation)(e?.target.value ?? '');

        if (e?.target.value) {
            MALE_SALUTATION.has(e.target.value)
                ? onGenderChange('Male')
                : e.target.value === ''
                ? onGenderChange('Other')
                : onGenderChange('Female');
        }
    };

    const onMedItemSaveToState = (
        catId: MEDICAL_HISTORY_CATEGORY,
        item: MHItem2,
        index?: number,
    ): void => {
        track('MH_ITEMS_SAVE', {
            from: 'pt-form-component',
            catId,
            item: item.id,
            status: item.status,
            name: item.name,
        });

        if (ptMHHistory) {
            setMedicalHistory({
                ...ptMHHistory,
                [catId]: cloneAndUpsert(item, ptMHHistory[catId], index),
            });
        }
    };

    const onMedItemSave = (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => {
        const { status } = item;

        if (status) {
            catId && onMedItemSaveToState(catId, item, index);
        } else {
            catId && onMedItemSaveToState(catId, { ...item, status: 'Active' }, index);
        }
    };

    const onMedItemDelete = (catId: MEDICAL_HISTORY_CATEGORY, itemId: string): void => {
        track('MH_ITEMS_DELETE', {
            from: 'pt-form-component',
            catId,
            item: itemId,
        });

        if (ptMHHistory) {
            setMedicalHistory({
                ...ptMHHistory,
                [catId]: cloneAndDelete(itemId, ptMHHistory[catId]),
            });
        }
    };

    const openConfigureMH = () => {
        setDialogVisibility(true, 'Medical History');
    };

    const isDoctor = getDoc()?.loginmeta?.role === 'DOCTOR';

    const showAbhaContent = !patientEditFlow;

    const setAbhaFields = (opt: { skipState: string; abhaId: string | undefined }) => {
        setShowAbhaCard(true);
        setAbhaId(opt.abhaId);
        setCreateSkipState(opt.skipState);
    };

    const validatePhoneNumber = (input: string) => {
        if (!input) setIsPhoneNumberError(false);
        if (phCode && input && phCodeEnum) {
            setIsPhoneNumberError &&
                setIsPhoneNumberError(!checkIfValidPhoneNumber(phCode, input, phCodeEnum));
        }
    };

    return formState === 'abha' ? (
        <CreateAbhaForm
            phCode={phCode}
            currMonthLoss={currMonthLoss}
            dfValues={dfValues}
            isAgeDOBError={!!isAgeDOBError}
            isDobError={isDobError}
            isNameError={isNameError}
            isPhoneNumberError={isPhoneNumberError}
            name={name}
            onAgeSelect={onAgeSelect}
            onAutoComplete={onAutoComplete}
            onDobChange={clearDob}
            onNameChange={onNameInputChange}
            onPhoneNumberChange={onPhoneInputChange}
            phoneNumber={phoneNumber}
            salutation={salutation}
            salutationChangeHandler={salutationChangeHandler}
            value={value}
            setAddPtFormState={setFormStateHandle}
            isGenderInputDisabled={isGenderInputDisabled}
            isGenderError={isGenderError}
            onGenderChange={onGenderChange}
            gender={gender}
            setAbhaFields={setAbhaFields}
            ageDays={ageDays}
            ageMonth={ageMonth}
            dob={dob}
            isModal={false}
            onDDDobChange={onDDDobChange}
            onMMDobChange={onMMDobChange}
            onYYYYDobChange={onYYYYDobChange}
            today={today}
            patientId={patientId}
            authHealthId={authHealthId}
            showSendOtpScreen={!!showSendOtpScreen}
            setShowSendOtpScreen={setShowSendOtpScreen}
            abhaId={abhaId}
            setIsPhoneNumberError={setIsPhoneNumberError}
            errorValidation={errorValidation}
            resetError={resetError}
            setAuthHealthId={setAuthHealthId}
            setShowAbhaCard={setShowAbhaCard}
            fromNdhmDiscovery={fromNdhmDiscovery}
            setAbhaPtOid={setAbhaOid}
        />
    ) : (
        <Fragment>
            <div className="flex justify-between">
                <div
                    className={`${patientEditFlow || showAbhaCard ? 'w-full' : 'w-33p'} ${
                        showAbhaCard ? 'self-center' : ''
                    }`}
                >
                    {showAbhaCard && (
                        <AbhaCard
                            abhaId={abhaId}
                            ageDays={ageDays}
                            ageMonth={ageMonth}
                            createSkipState={createSkipState}
                            dob={dob}
                            gender={gender}
                            name={name}
                            phoneNumber={phoneNumber}
                            className="w-50p"
                        />
                    )}
                    {!showAbhaCard && (
                        <div className="flex space-x-16">
                            <div className={`space-y-16 ${patientEditFlow ? 'w-40p' : 'w-full'}`}>
                                <PatientPhoneAutoSuggest
                                    onSelect={onPatientAutoComplete}
                                    onInputChange={onPhoneInputChange}
                                    onCountryCodeChange={onCountryCodeChange}
                                    inputValue={phoneNumber || ''}
                                    disabled={isPhoneInputDisabled}
                                    suggestionsEnabled={false}
                                    phCode={phCode}
                                    setIsPhoneNumberError={setIsPhoneNumberError}
                                    onBlur={validatePhoneNumber}
                                    phCodeEnum={phCodeEnum}
                                    cid={params.cId}
                                    isAbdmEnabled={isAbdmEnabled}
                                    fromNdhmDiscovery={fromNdhmDiscovery}
                                />

                                {fromNdhmDiscovery &&
                                name &&
                                phoneNumber?.length === 10 &&
                                gender ? (
                                    <div
                                        className="rounded-8 bg-darwin-neutral_new-50 px-10 py-8 gap-4 flex items-center"
                                        style={{ width: 'fit-content' }}
                                    >
                                        <span className="self-start">{AbhaIconNotLinked}</span>
                                        <div className="flex flex-col gap-4">
                                            <p className="text-13 font-700">
                                                {name} | {gender?.slice(0, 1)}
                                            </p>
                                            <p className="text-12 font-400">
                                                {phoneNumber} | {authHealthId}
                                            </p>
                                        </div>
                                    </div>
                                ) : null}

                                <div className="mt-4">
                                    {isPhoneNumberError && getErrorComp(INVALID_PHONE_NUMBER)}
                                </div>
                            </div>
                        </div>
                    )}
                    {!showAbhaCard && (
                        <div className={`${patientEditFlow ? 'w-60p' : 'w-full'}`}>
                            <div>
                                <div className="space-y-4">
                                    <div className="flex justify-between">
                                        <p className="text-neutral-3 text-14 font-700">
                                            Patient Name <span> *</span>
                                        </p>
                                        {isNameInputDisabled && (
                                            <span className="text-12 self-end mb-2 text-status-error select-none">
                                                (can not be edited)
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-16">
                                        {salutation && (
                                            <div className="add-pt-dob-select w-33p">
                                                <select
                                                    required
                                                    value={
                                                        sanitizeSalutation(dfValues['salutation'] as typeof SALUTATION[number])
                                                    }
                                                    onChange={salutationChangeHandler}
                                                    className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                                >
                                                    <option value="">-</option>
                                                    {SALUTATION.map((v, i) => (
                                                        <option key={v + i + 1} value={v}>
                                                            {v}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="mimic-box"></div>
                                            </div>
                                        )}
                                        <div className="w-full">
                                            <PatientNameAutoSuggest
                                                onSelect={onPatientAutoComplete}
                                                onInputChange={onNameInputChange}
                                                inputValue={name || ''}
                                                disabled={isNameInputDisabled}
                                                suggestionsEnabled={false}
                                                isPhCode={isPhCode}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    {isNameError && getErrorComp('Patient name is required')}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* {!patientEditFlow && !showUseABDM && isDoctor && (
                    <div className="flex border w-386 border-darwin-neutral_new-200 rounded-10 flex-col">
                        <NoABDMPopver />
                    </div>
                )} */}
                {/* {!patientEditFlow && showUseABDM && !showAbhaCard && (
                    <>
                        {isSendAbhaOtp && showSendOtp && (
                            <button
                                className={`mt-24 mr-24 cursor-pointer flex py-8 px-16 gap-4 items-center justify-center rounded-4 bg-darwin-accent-bright-blue-800 ${
                                    checkIfValidPhoneNumber(phCode, phoneNumber) ? '' : 'opacity-50'
                                }`}
                                style={{ width: 'fit-content', height: 'fit-content' }}
                                onClick={() => {
                                    if (checkIfValidPhoneNumber(phCode, phoneNumber)) {
                                        setShowSendOtp(false);
                                        onSendOtp && onSendOtp();
                                    }
                                }}
                                disabled={
                                    checkIfValidPhoneNumber(phCode, phoneNumber) ? false : true
                                }
                            >
                                <p className="text-white text-14 font-700">Send ABHA OTP</p>
                                <svg
                                    className="fill-white font-700"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    width={12}
                                    height={12}
                                >
                                    <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                                </svg>
                            </button>
                        )}

                        {isAbdmEnabled && (
                            <div
                                className={`relative flex justify-between border border-darwin-neutral_new-200 rounded-10 flex-col ${
                                    isSendAbhaOtp && showSendOtp ? 'hidden' : ''
                                }`}
                            >
                                <div className="max-w-400 overflow-scroll hide-scrollbar">
                                    <div className="p-12 pb-0" id={ABHA_IN_DOCTOOL_CONTAINER}></div>
                                </div>
                                <CurrMonthLossStrip
                                    currMonthLoss={currMonthLoss}
                                    className="mt-4 gap-8"
                                />
                            </div>
                        )}
                    </>
                )} */}
                <TakePatientPic pic={pic} setPic={setPic} />
            </div>
            {/* ---- */}
            {!showAbhaCard && (
                <div
                    className={`flex items-start ${
                        showUseABDM ? 'w-50p sm:w-full' : 'w-full mt-16'
                    } space-x-32`}
                >
                    <div className="w-33p">
                        <div className="space-y-4">
                            <div className="flex justify-between">
                                <p className="text-neutral-3 text-14 font-700">Age</p>
                                {isAgeInputDisabled && (
                                    <span className="text-12 self-end mb-2 text-status-error select-none">
                                        (can not be edited)
                                    </span>
                                )}
                            </div>
                            <NewPatientAgeInput
                                value={value}
                                disabled={isAgeInputDisabled}
                                onSelect={onAgeSelect}
                            />
                        </div>
                    </div>

                    <p className="mt-32 font-700 whitespace-nowrap text-neutral-5">OR</p>

                    <div className="w-328">
                        <div className="space-y-4">
                            <div className="flex justify-between">
                                <p className="text-neutral-3 text-14 font-700">Date of Birth</p>
                                {isAgeInputDisabled && (
                                    <span className="text-12 self-end mb-2 text-status-error select-none">
                                        (can not be edited)
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center space-x-8">
                                <div className="add-pt-dob-select w-33p">
                                    <select
                                        required
                                        value={dob?.dd || '0'}
                                        onChange={onDDDobChange}
                                        className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                        disabled={isAgeInputDisabled}
                                    >
                                        <option value="">DD</option>
                                        {[...Array(31).keys()].map((_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                {i + 1}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="mimic-box"></div>
                                </div>
                                <div className="add-pt-dob-select w-33p">
                                    <select
                                        required
                                        value={dob?.mm || '0'}
                                        onChange={onMMDobChange}
                                        className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                        disabled={isAgeInputDisabled}
                                    >
                                        <option value="">MM</option>
                                        <option value="1">Jan</option>
                                        <option value="2">Feb</option>
                                        <option value="3">Mar</option>
                                        <option value="4">Apr</option>
                                        <option value="5">May</option>
                                        <option value="6">Jun</option>
                                        <option value="7">Jul</option>
                                        <option value="8">Aug</option>
                                        <option value="9">Sep</option>
                                        <option value="10">Oct</option>
                                        <option value="11">Nov</option>
                                        <option value="12">Dec</option>
                                    </select>
                                    <div className="mimic-box"></div>
                                </div>
                                <div className="add-pt-dob-select w-33p">
                                    <select
                                        required
                                        value={dob?.yyyy || '0'}
                                        onChange={onYYYYDobChange}
                                        className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                        disabled={isAgeInputDisabled}
                                    >
                                        <option value="">YYYY</option>
                                        {[...Array(111).keys()].map((_, i) => (
                                            <option key={today.year() - i} value={today.year() - i}>
                                                {today.year() - i}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="mimic-box"></div>
                                </div>
                            </div>
                        </div>
                        {isDobError && getErrorComp('Invalid Date of Birth')}
                        {isAgeDOBError && getErrorComp('Age or date of birth is required')}
                    </div>
                </div>
            )}

            {/* -------- */}

            {!showAbhaCard && (
                <div className="w-33p mt-16">
                    <div className="space-y-4">
                        <div className="flex justify-between">
                            <p className="text-neutral-3 font-700 text-14">
                                Gender<span> *</span>
                            </p>
                            {isGenderInputDisabled && (
                                <span className="text-12 self-end mb-2 text-status-error select-none">
                                    (can not be edited)
                                </span>
                            )}
                        </div>
                        <div className="flex space-x-12">
                            {GENDERS.map((g) => (
                                <button
                                    key={g}
                                    tabIndex={0}
                                    onClick={() => onGenderChange(g)}
                                    className={`text-14 font-700 inline-flex items-center rounded-24 px-16 py-6 focus:border-neutral-black focus:outline-none ${
                                        gender?.toLowerCase() === g?.toLowerCase()
                                            ? ' text-white bg-neutral-black'
                                            : ' border text-neutral-5 border-neutral-5'
                                    }`}
                                    disabled={isGenderInputDisabled}
                                >
                                    {g}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="w-33p mt-4">
                        {isGenderError && getErrorComp('Gender is required')}
                    </div>
                </div>
            )}

            {/* -------- */}

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {uhidDForm && (
                    <div key={uhidDForm.key} className="mt-16 space-y-4 w-66p">
                        <p className="text-neutral-3 font-700 text-14">{uhidDForm.label}</p>
                        <div className="flex space-x-16 items-center w-full">
                            <div className="w-50p">
                                <PatientUhidAutoSuggest
                                    onSelect={onPatientAutoComplete}
                                    onInputChange={onUhidInputChange}
                                    inputValue={uhid || ''}
                                    suggestionsEnabled={false}
                                    isPhCode={isPhCode}
                                />
                            </div>
                            <SuggestUHID
                                setUHID={onDFChange(uhidDForm)}
                                isAlreadyPresent={
                                    (dfValues[uhidDForm.key]?.toString() || '').length > 0
                                }
                            />
                        </div>
                    </div>
                )}
                {patientEditFlow && patientId && <RelationsInEditPatient pid={patientId} />}
                {showMedicalHistoryInAddPt && (
                    <div
                        className="flex flex-col gap-4 mt-16 py-8 w-full mh_component_add_pt"
                        id={MEDICAL_HISTORY_CONTAINER}
                    >
                        <div className="flex items-center gap-16">
                            <p className="text-neutral-3 font-700 text-14">
                                Patient Medical History
                            </p>
                            <Checkbox
                                className="mh-add-pt-checkbox text-13"
                                onChange={toggleHasNoMedicalHistory}
                                checked={hasNoMedicalHistory}
                            >
                                No known medical history
                            </Checkbox>
                        </div>
                        <CommonItemsMH
                            onItemSave={onMedItemSave}
                            onItemDelete={onMedItemDelete}
                            patientHistoryData={ptMHHistory}
                            isMedicalHistorySection={true}
                            medicalHistoryOptions={medicalHistoryOptions}
                            StatusMessage={StatusMessage}
                            medicalHistoryConfigData={medicalHistoryConfigData}
                            onConfigure={openConfigureMH}
                            hideCommonItems={hasNoMedicalHistory}
                            showMHConfigure={showMHConfigure}
                        />
                    </div>
                )}
                {dFormWithoutUHID && dFormWithoutUHID.length > 0 ? (
                    dFormWithoutUHID
                        .filter((t) => t.key !== 'salutation')
                        .map((df) => {
                            if (df.key === 'referredbynumber') {
                                return null;
                            }

                            if (df.key === 'referredby') {
                                const referredByNumber = DYNAMIC_FORMS.find(
                                    (form) => form.key === 'referredbynumber',
                                );
                                return (
                                    <div className="mh_component_add_pt">
                                        <div className="mt-16 space-y-4 w-66p">
                                            <PatientReferralAutoSuggest
                                                onSelect={(data) => {
                                                    onAddEkaRefer(
                                                        data?.id,
                                                        data?.name,
                                                        data?.number ||
                                                            (dfValues?.referredbynumber as string) ||
                                                            '',
                                                    );
                                                }}
                                                onInputChange={(text) => {
                                                    onDFChange(df)(text);
                                                }}
                                                inputValue={
                                                    (dfValues['referredby'] || '') as string
                                                }
                                                disabled={isPhoneInputDisabled}
                                                suggestionsEnabled={!name && !uhid}
                                            />
                                        </div>
                                        {referredByNumber && (
                                            <div
                                                key={referredByNumber.key}
                                                className="mt-16 space-y-4 w-66p"
                                            >
                                                <PatientPhoneNumber
                                                    onSelect={(suggestion) => {
                                                        onDFChange(referredByNumber)(
                                                            (suggestion?.value || '').toString(),
                                                        );
                                                    }}
                                                    onInputChange={(text) => {
                                                        onDFChange(referredByNumber)(
                                                            text.toString(),
                                                        );
                                                    }}
                                                    inputValue={
                                                        dfValues[
                                                            referredByNumber.key
                                                        ]?.toString() || ''
                                                    }
                                                    phoneNumbers={
                                                        dyFormSuggestions[
                                                            referredByNumber.key.toLowerCase()
                                                        ] as number[]
                                                    }
                                                    isPhoneNumberError={isReferralPhoneNumberError}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <div key={df.key} className="mt-16 space-y-4 w-66p">
                                    <p className="text-neutral-3 font-700 text-14">{df.label}</p>
                                    <div className="flex space-x-16 items-center w-full">
                                        {df.key === 'maritalstatus' ? (
                                            <div className="add-pt-dob-select w-50p">
                                                <select
                                                    required
                                                    value={dfValues['maritalstatus'] || ''}
                                                    onChange={(e) => onDFChange(df)(e.target.value)}
                                                    className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                                >
                                                    <option value="">-</option>
                                                    {MARITAL_STATUS.map((v, i) => (
                                                        <option key={v + i + 1} value={v}>
                                                            {v}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="mimic-box"></div>
                                            </div>
                                        ) : df.key === 'rpp' ? (
                                            <div className="add-pt-dob-select w-50p">
                                                <select
                                                    required
                                                    value={dfValues['rpp'] || ''}
                                                    onChange={(e) => onDFChange(df)(e.target.value)}
                                                    className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                                >
                                                    <option value="">-</option>
                                                    {FAMILY_MEMBERS_NAME.map((v) => (
                                                        <option key={v.id} value={v.value}>
                                                            {v.value}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="mimic-box"></div>
                                            </div>
                                        ) : df.key === 'lang' ? (
                                            <div className="add-pt-dob-select w-50p">
                                                <select
                                                    required
                                                    value={dfValues[df.key] || ''}
                                                    onChange={(e) => onDFChange(df)(e.target.value)}
                                                    className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                                >
                                                    <option value="">-</option>
                                                    {LANGS.map((v) => (
                                                        <option key={v.code} value={v.code}>
                                                            {v.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="mimic-box"></div>
                                            </div>
                                        ) : (
                                            <div className="add-pt-input w-50p">
                                                <input
                                                    placeholder={df.label}
                                                    value={dfValues[df.key]?.toString() || ''}
                                                    onChange={(e) => onDFChange(df)(e.target.value)}
                                                    className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                                                    list={`new-pt-f-${df.key}`}
                                                    autoComplete="nope"
                                                ></input>
                                                <datalist id={`new-pt-f-${df.key}`}>
                                                    {dyFormSuggestions[df.key.toLowerCase()]?.map(
                                                        (t) => (
                                                            <option value={t}></option>
                                                        ),
                                                    )}
                                                </datalist>
                                                <div className="mimic-box"></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                ) : showAddFields ? (
                    <button
                        onClick={onAddFieldsClick}
                        className="border-b border-transparent cursor-pointer text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700 focus:outline-none mt-16"
                    >
                        + Add Fields
                    </button>
                ) : null}
            </motion.div>
        </Fragment>
    );
};

export const SuggestUHID: React.FC<{
    setUHID: (value: string) => void;
    isAlreadyPresent: boolean;
    isModal?: boolean;
}> = (props) => {
    const { setUHID, isAlreadyPresent } = props;
    const [apiState, setApiState] = useState<'loading' | 'success' | 'error' | ''>('');
    const [setupDialogVisible, setSetupDialogVisible] = useState(false);

    const generateNew = () => {
        setApiState('loading');
        fetchUHIDSuggest()
            .then((t) => {
                if (t.success && t.uhid) {
                    setUHID(t.uhid);
                    setApiState('success');
                    track('UHID_GENERATE_SUCCESS', { uhid: t.uhid });
                } else {
                    setApiState('error');
                    track('ERROR', { on: 'UHID_GENERATE', error: t.error });
                }
            })
            .catch((e) => {
                setApiState('error');
                track('ERROR', { on: 'UHID_GENERATE', error: (e as Error).message });
            });
    };

    const generateUHID = () => {
        if (isAlreadyPresent) {
            const t = Modal.confirm({
                title: 'UHID already exists for this patient. Continue to generate a new UHID?',
                okText: 'Yes',
                onOk: () => {
                    track('UHID_GENERATE_OVERRIDE_YES');
                    generateNew();
                    t.destroy();
                },
                okButtonProps: { className: 'primary-btn font-lato' },
                cancelButtonProps: { className: 'neutral-btn font-lato' },
                keyboard: true,
                closable: true,
                cancelText: 'No',
                onCancel: () => {
                    track('UHID_GENERATE_OVERRIDE_CANCEL');
                    t.destroy();
                },
            });

            setTimeout(() => {
                const el: HTMLDivElement = document.getElementsByClassName(
                    'ant-modal-wrap',
                )[0] as HTMLDivElement;
                el?.focus();
            }, 200);

            return;
        }

        generateNew();
    };

    const onSetup = () => {
        setSetupDialogVisible(true);
    };

    const onSetupHide = () => {
        setApiState('');
        setSetupDialogVisible(false);
    };

    const dialog = (
        <UHIDSetup onSuccess={onSetupHide} closeMe={onSetupHide} visible={setupDialogVisible} />
    );

    if (apiState === '' || apiState === 'loading' || apiState === 'success') {
        return (
            <Fragment>
                {dialog}

                <button
                    onClick={generateUHID}
                    className={`border-b border-transparent cursor-pointer  ${
                        props.isModal
                            ? 'font-400 w-120 text-darwin-accent-bright-blue-800'
                            : 'font-700 hover:text-actionPrimary-hover hover:border-actionPrimary-hover text-actionPrimary'
                    } focus:outline-none`}
                    disabled={apiState === 'loading'}
                >
                    {apiState === 'loading' ? 'Generating' : 'Generate'} UHID
                </button>
            </Fragment>
        );
    }

    if (apiState === 'error') {
        return (
            <Fragment>
                {dialog}
                <div
                    className={`px-12 py-4 flex items-center ${
                        props.isModal && 'w-full ml-8'
                    } space-x-4 bg-status-errorBG rounded-8`}
                >
                    <svg
                        className="w-14 h-14 fill-status-error"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                    >
                        <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM232 152C232 138.8 242.8 128 256 128s24 10.75 24 24v128c0 13.25-10.75 24-24 24S232 293.3 232 280V152zM256 400c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 385.9 273.4 400 256 400z"></path>
                    </svg>

                    <div className="text-13 text-status-error flex space-x-8">
                        <button
                            onClick={onSetup}
                            className="border-b border-transparent cursor-pointer text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700 focus:outline-none"
                        >
                            Click here
                        </button>
                        <span>to setup auto UHID generation.</span>
                    </div>
                </div>
            </Fragment>
        );
    }

    return null;
};

export const UHIDSetupModal: React.FC<{
    apiState: 'loading' | 'success' | 'error' | '';
    prefix: string;
    startFrom: string;
    visible: boolean;
    onSave: () => Promise<void>;
    closeMe?: () => void;
    setPrefix: any;
    setStartFrom: any;
}> = (props) => {
    const { apiState, prefix, startFrom, visible, onSave, setPrefix, setStartFrom } = props;

    if (!props.closeMe) {
        return null;
    }

    return (
        <Modal
            bodyStyle={{
                overflowY: 'auto',
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                padding: '0px',
            }}
            okButtonProps={{
                className: 'primary-btn font-lato',
                loading: apiState === 'loading',
                disabled: !prefix || !startFrom,
                style: { color: 'white' },
            }}
            cancelButtonProps={{ className: 'neutral-btn font-lato' }}
            className="flex flex-col"
            title={<div className="font-lato w-full font-700">{'UHID Configuration'}</div>}
            visible={visible}
            onOk={onSave}
            okText="Save"
            onCancel={props.closeMe}
        >
            <div className="flex space-x-16 p-16 items-center">
                <Input
                    placeholder="e.g SK"
                    style={{ width: '50%' }}
                    className=""
                    value={prefix}
                    required
                    onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                    addonBefore={'Prefix'}
                />
                <Input
                    placeholder="e.g 10000"
                    value={startFrom}
                    required
                    onChange={(e) => {
                        if (e.target.value && isNaN(parseInt(e.target.value))) return;
                        setStartFrom(e.target.value);
                    }}
                    addonBefore={'Starts From'}
                />
            </div>
        </Modal>
    );
};

export const UHIDSetup: React.FC<{
    visible?: boolean;
    onSuccess: () => void;
    closeMe?: () => void;
    mode?: 'modal' | 'page';
}> = (props) => {
    const { onSuccess, visible = false, mode = 'modal' } = props;
    const [prefix, setPrefix] = useState('');
    const [startFrom, setStartFrom] = useState('');
    const [apiState, setApiState] = useState<'loading' | 'success' | 'error' | ''>('');

    const onSave = async () => {
        if (!prefix || !startFrom) {
            return;
        }

        if (isNaN(parseInt(startFrom))) {
            return;
        }

        setApiState('loading');
        pushUHIDConfig({ prefix: prefix, startfrom: parseInt(startFrom) })
            .then((t) => {
                if (t.success) {
                    setApiState('success');
                    onSuccess();
                    track('SAVE_UHID_CONFIG', { prefix, startFrom });
                } else {
                    setApiState('error');
                    notification.error({
                        message: `Failed to update UHID configuration`,
                        description: t.error,
                        placement: 'topRight',
                        duration: 5,
                    });

                    track('ERROR', { on: 'pushUHIDConfig', what: t.error });
                }

                setTimeout(() => {
                    const el: HTMLDivElement = document.getElementsByClassName(
                        'ant-modal-wrap',
                    )[0] as HTMLDivElement;
                    el?.focus();
                }, 200);
            })
            .catch((e) => {
                setApiState('error');
                console.error(e);

                setTimeout(() => {
                    const el: HTMLDivElement = document.getElementsByClassName(
                        'ant-modal-wrap',
                    )[0] as HTMLDivElement;
                    el?.focus();
                }, 200);

                track('ERROR', { on: 'pushUHIDConfig', what: e.message });
                notification.error({
                    message: `Failed to update UHID configuration`,
                    placement: 'topRight',
                    duration: 5,
                });
            });

        return;
    };

    useEffect(() => {
        fetchUHIDConfig()
            .then((t) => {
                if (t.success) {
                    setPrefix(t.config.prefix);
                    setStartFrom(t.config.startfrom + '');
                }
            })
            .catch(console.error);
    }, []);

    return (
        <Fragment>
            {mode === 'modal' ? (
                <UHIDSetupModal
                    apiState={apiState}
                    closeMe={props.closeMe}
                    onSave={onSave}
                    prefix={prefix}
                    setPrefix={setPrefix}
                    setStartFrom={setStartFrom}
                    startFrom={startFrom}
                    visible={visible}
                />
            ) : (
                <div className="w-full">
                    <p className="text-16 font-700 text-neutral-black px-24 pt-24 pb-0">
                        Setup UHID
                    </p>
                    <div className="flex flex-col gap-24 p-24">
                        <div className="flex items-center gap-16">
                            <div className="flex flex-col gap-4">
                                <label className="text-13 font-700">Prefix</label>
                                <Input
                                    placeholder="e.g SK"
                                    value={prefix}
                                    required
                                    onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                                    className="w-200 h-36 rounded-8 py-10"
                                />
                            </div>
                            <div className="flex flex-col gap-4">
                                <label className="text-13 font-700">Starts from</label>
                                <Input
                                    placeholder="e.g 10000"
                                    value={startFrom}
                                    required
                                    onChange={(e) => {
                                        if (e.target.value && isNaN(parseInt(e.target.value)))
                                            return;
                                        setStartFrom(e.target.value);
                                    }}
                                    className="w-200 h-36 rounded-8 py-10"
                                />
                            </div>
                        </div>
                        <button
                            className="w-110 py-9 px-16 px-16 btn-small rounded-8 bg-darwin-accent-bright-blue-800 text-white text-14 font-700"
                            style={{ width: 110 }}
                        >
                            <span className="w-full text-center text-13" onClick={onSave}>
                                Save changes
                            </span>
                        </button>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default PatientInfoForm;
